import React, { useState } from "react";
import { RemixBuilderContext } from "../remix/shared/RemixBuilderContext";
import { canUseAiRemixTemplate } from "../../utils/canUseAiRemixText";

const CTA_TYPE_OPTIONS = [
  { value: "page", label: "Landing Page" },
  { value: "overlay", label: "Overlay Popup" },
  { value: "hook", label: "Hook Popup" },
  { value: "bar", label: "Sticky Bar" },
  { value: "inline", label: "Embed" },
];

function resolveCtaType(initialCtaType) {
  if (!initialCtaType) return CTA_TYPE_OPTIONS[0];
  const found = CTA_TYPE_OPTIONS.find((o) => o.value === initialCtaType);
  return found || CTA_TYPE_OPTIONS[0];
}

export const TemplatePreviewModalProvider = (props) => {
  const {
    templateData,
    website,
    subscription,
    account,
    current_user,
    remix_mappings,
    styles = {},
    ctaType,
    remixPrompt,
    detectedAssetTheme,
  } = props;

  // Auto-enable AI Remix when eligible (any template) and a remix prompt is present.
  const canAutoEnableAiRemix = canUseAiRemixTemplate({
    currentUser: current_user,
    website,
    subscription,
    account,
    template: templateData?.template,
  });

  const initialContext = {
    current_user,
    website,
    subscription,
    account,
    currentMonthYear: moment().format("MMMM YYYY"),
    environment:
      props.environment ??
      (typeof window !== "undefined" ? window.environment : undefined),
    filestack_api_key: props.FILESTACK_API_KEY,
    uploaded_sections: [],
    matched_sections: templateData
      ? [
          {
            id: crypto.randomUUID(),
            position: 1,
            selected: true,
            template: templateData.template,
            tags: [],
          },
        ]
      : [],
    section_templates: [],
    current_step: 1,
    from_scratch: true,
    remix_settings: {
      cta_type: resolveCtaType(ctaType),
      campaign_name: templateData?.template?.name || "",
      styles: { ...styles },
      options: {
        remix_all_text: canAutoEnableAiRemix && !!remixPrompt,
        remix_tone: remixPrompt || "",
        remix_asset_urls: [],
        excluded_element_types: [],
      },
    },
    settings: {
      max_section_remixes: 50,
      used_section_remixes: 0,
    },
    remix_mappings: remix_mappings || {},
    remix_loading: null,
    detected_asset_theme: detectedAssetTheme || null,
    detected_theme: detectedAssetTheme || null,
  };

  const [contextData, setContextData] = useState(initialContext);

  const builder = {
    goToStep: (step) => {
      setContextData((prev) => ({ ...prev, current_step: step }));
    },

    goToPreviousStep: () => {
      setContextData((prev) => ({
        ...prev,
        current_step: Math.max(1, prev.current_step - 1),
      }));
    },

    goToNextStep: () => {
      setContextData((prev) => ({
        ...prev,
        current_step: Math.min(2, prev.current_step + 1),
      }));
    },

    updateRemixSettings: (key, value) => {
      setContextData((prev) => ({
        ...prev,
        remix_settings: {
          ...prev.remix_settings,
          [key]: value,
        },
      }));
    },

    setRemixLoading: (loadingState) => {
      setContextData((prev) => ({
        ...prev,
        remix_loading: loadingState,
      }));
    },
  };

  return (
    <RemixBuilderContext.Provider
      value={[contextData, builder, setContextData]}
    >
      {props.children}
    </RemixBuilderContext.Provider>
  );
};
